import { PALETTE } from '../../engine/palettes.js';
import {
  applyLinkedCameraDistance,
  cameraDistanceForLinkedFov,
  DEFAULT_SETTINGS,
  resetSettings,
  saveSettings,
  type VisualSettings,
} from '../../engine/Settings.js';
import type { SettingsSection } from '../SettingsModal.js';
import { addDivider, addNote, addSectionTitle, addSlider, addToggle } from './formHelpers.js';

/** Lifted from the old SettingsPanel: every camera / FOV / blur / fog / palette
 *  control. The six diagnostic toggles are NOT here — they moved to the Debug
 *  section. The `onChange` callback is fired with the set of keys that mutated,
 *  matching the old SettingsPanel's signature so main.ts's existing handler
 *  (re-apply to camera / PostFx / scene fog) works unchanged. */
export function createVisualSection(
  settings: VisualSettings,
  onChange: (changed: Set<keyof VisualSettings>) => void,
): SettingsSection {
  let bodyEl: HTMLElement | null = null;
  // Held so the link-toggle handler can rewrite the camera-distance display when
  // FoV-linking gets turned on (the slider would otherwise show a stale value).
  let cameraDistanceValue: HTMLElement | null = null;
  let cameraDistanceSlider: HTMLInputElement | null = null;

  const rebuild = (parent: HTMLElement): void => {
    parent.replaceChildren();
    bodyEl = parent;

    addToggle(parent, 'Photo mode', settings.photoMode, (checked) => {
      settings.photoMode = checked;
      saveSettings(settings);
      onChange(new Set<keyof VisualSettings>(['photoMode']));
    });
    addNote(parent, 'Hides the minimap, location bar, and delivery UI for clean screenshots. Hotkey: P.');

    addDivider(parent);

    // ---- FoV (custom: rewrites linked camera distance on input) ----
    const fov = addSlider(
      parent,
      'Field of view',
      50,
      110,
      1,
      settings.fovDegrees,
      (v) => `${v}°`,
      (v) => {
        const changed = new Set<keyof VisualSettings>(['fovDegrees']);
        settings.fovDegrees = v;
        if (settings.linkFovDistance) {
          settings.cameraDistance = cameraDistanceForLinkedFov(v);
          changed.add('cameraDistance');
          if (cameraDistanceSlider) cameraDistanceSlider.value = String(settings.cameraDistance);
          if (cameraDistanceValue) {
            cameraDistanceValue.textContent = `${settings.cameraDistance.toFixed(1)} m`;
          }
        }
        saveSettings(settings);
        onChange(changed);
      },
    );
    void fov; // not retained — only camera-distance handles are reused

    addToggle(parent, 'Keep FoV and camera distance linked', settings.linkFovDistance, (checked) => {
      const changed = new Set<keyof VisualSettings>(['linkFovDistance']);
      settings.linkFovDistance = checked;
      if (checked) {
        applyLinkedCameraDistance(settings);
        changed.add('cameraDistance');
      }
      saveSettings(settings);
      onChange(changed);
      if (bodyEl) rebuild(bodyEl); // disabled-state on camera-distance slider flips
    });

    addToggle(parent, 'Speed-FoV boost', settings.speedFovBoost, (checked) => {
      settings.speedFovBoost = checked;
      saveSettings(settings);
      onChange(new Set<keyof VisualSettings>(['speedFovBoost']));
    });

    addDivider(parent);

    addToggle(parent, 'Camera shake', settings.cameraShakeEnabled, (checked) => {
      settings.cameraShakeEnabled = checked;
      saveSettings(settings);
      onChange(new Set<keyof VisualSettings>(['cameraShakeEnabled']));
      if (bodyEl) rebuild(bodyEl); // intensity slider disabled-state flips
    });
    addSlider(
      parent,
      'Shake intensity',
      0,
      2,
      0.05,
      settings.cameraShakeIntensity,
      (v) => v.toFixed(2),
      (v) => {
        settings.cameraShakeIntensity = v;
        saveSettings(settings);
        onChange(new Set<keyof VisualSettings>(['cameraShakeIntensity']));
      },
      { disabled: !settings.cameraShakeEnabled },
    );

    addDivider(parent);

    addToggle(parent, 'Camera lag', settings.cameraLagEnabled, (checked) => {
      settings.cameraLagEnabled = checked;
      saveSettings(settings);
      onChange(new Set<keyof VisualSettings>(['cameraLagEnabled']));
    });
    const cd = addSlider(
      parent,
      'Camera distance',
      4,
      15,
      0.5,
      settings.cameraDistance,
      (v) => `${v.toFixed(1)} m`,
      (v) => {
        settings.cameraDistance = v;
        saveSettings(settings);
        onChange(new Set<keyof VisualSettings>(['cameraDistance']));
      },
      { disabled: settings.linkFovDistance },
    );
    cameraDistanceSlider = cd.slider;
    cameraDistanceValue = cd.value;

    addSlider(
      parent,
      'Camera height',
      1,
      6,
      0.25,
      settings.cameraHeight,
      (v) => `${v.toFixed(2)} m`,
      (v) => {
        settings.cameraHeight = v;
        saveSettings(settings);
        onChange(new Set<keyof VisualSettings>(['cameraHeight']));
      },
    );
    addSlider(
      parent,
      'Accel-based distance',
      0,
      2,
      0.05,
      settings.accelDistanceAmount,
      (v) => v.toFixed(2),
      (v) => {
        settings.accelDistanceAmount = v;
        saveSettings(settings);
        onChange(new Set<keyof VisualSettings>(['accelDistanceAmount']));
      },
    );

    addDivider(parent);

    addSlider(
      parent,
      'Speed blur',
      0,
      2,
      0.05,
      settings.radialBlurIntensity,
      (v) => v.toFixed(2),
      (v) => {
        settings.radialBlurIntensity = v;
        saveSettings(settings);
        onChange(new Set<keyof VisualSettings>(['radialBlurIntensity']));
      },
    );

    addDivider(parent);

    addSlider(
      parent,
      'Car power',
      0.1,
      1.0,
      0.05,
      settings.carPower,
      (v) => v.toFixed(2),
      (v) => {
        settings.carPower = v;
        saveSettings(settings);
        onChange(new Set<keyof VisualSettings>(['carPower']));
      },
    );

    addDivider(parent);

    addToggle(parent, 'Classic colors', settings.useClassicColors, (checked) => {
      settings.useClassicColors = checked;
      saveSettings(settings);
      onChange(new Set<keyof VisualSettings>(['useClassicColors']));
    });
    addNote(parent, 'Darker greens + bluer sky (master palette). Reloads on change.');

    addDivider(parent);

    addSlider(
      parent,
      'Fog distance',
      0.5,
      2.5,
      0.1,
      settings.fogDistanceMul,
      (v) => `${Math.round(PALETTE.fogEnd * v)} m`,
      (v) => {
        settings.fogDistanceMul = v;
        saveSettings(settings);
        onChange(new Set<keyof VisualSettings>(['fogDistanceMul']));
      },
    );

    addDivider(parent);
    addSectionTitle(parent, 'Rendering');

    const diagKeys: Array<{ key: keyof VisualSettings; label: string }> = [
      { key: 'diagShadowsEnabled', label: 'Shadows' },
      { key: 'diagPostFxEnabled', label: 'Post-processing' },
      { key: 'diagSkyEnabled', label: 'Sky' },
      { key: 'diagTerrainShadingEnabled', label: 'Terrain shading' },
      { key: 'diagVehicleFxEnabled', label: 'Vehicle FX' },
      { key: 'diagCameraModsEnabled', label: 'Camera mods' },
    ];
    for (const { key, label } of diagKeys) {
      addToggle(parent, label, settings[key] as boolean, (checked) => {
        (settings as unknown as Record<string, unknown>)[key as string] = checked;
        saveSettings(settings);
        onChange(new Set<keyof VisualSettings>([key]));
      });
    }
    addToggle(parent, 'Grass', settings.grassEnabled, (checked) => {
      settings.grassEnabled = checked;
      saveSettings(settings);
      onChange(new Set<keyof VisualSettings>(['grassEnabled']));
    });
    addNote(parent, 'Terrain shading applies to newly loaded chunks. Grass = near-field 3D blades.');
    addToggle(parent, 'Anime style', settings.animeStyleEnabled, (checked) => {
      settings.animeStyleEnabled = checked;
      saveSettings(settings);
      onChange(new Set<keyof VisualSettings>(['animeStyleEnabled']));
    });
    addNote(parent, 'Bold ink outlines + cel-banded shading. Needs Post-processing on.');

    addDivider(parent);

    const reset = document.createElement('button');
    reset.className = 'ghost settings-reset';
    reset.textContent = 'Reset to defaults';
    reset.addEventListener('click', () => {
      const before = { ...settings };
      resetSettings(settings);
      const changed = new Set<keyof VisualSettings>();
      for (const key of Object.keys(settings) as Array<keyof VisualSettings>) {
        if (before[key] !== settings[key]) changed.add(key);
      }
      saveSettings(settings);
      onChange(changed);
      if (bodyEl) rebuild(bodyEl);
    });
    parent.appendChild(reset);

    // Suppress unused-var noise for DEFAULT_SETTINGS (re-export consumers).
    void DEFAULT_SETTINGS;
  };

  return {
    id: 'visual',
    title: 'Visual',
    build(parent: HTMLElement): void {
      rebuild(parent);
    },
  };
}
